<template>
  <div class="project-show">
    <page-header :title="projectData.name">
      <template #action>
        <v-btn
          :to="{ name: 'projects' }"
          link
          color="button"
          class="fw-400"
        >
          BACK
        </v-btn>
      </template>
      <template #breadcrumbs>
        <router-link :to="{ name: 'projects' }">
          Projects
        </router-link>
        <span>/ {{ projectData.name }}</span>
      </template>
    </page-header>

    <div class="project-show__content">
      <ProjectFinanceSummary
        :summary-data="projectRecentSummary"
        :route-link="{ name: 'project-finance', params: { projectId: id }}"
      />

      <ProjectDetailsForm
        ref="projectDetailsForm"
        :project-base="projectData"
        :submit-name="submitName"
        @submit="update"
      />

      <v-row>
        <v-col cols="12" lg="6" class="pt-0">
          <ProjectNotesCard :notes="projectData.notes || ''"
            @edit="openNotesModal"
          />
        </v-col>

        <v-col cols="12" lg="6" class="pt-0">
          <AttachmentsCard
            :attachments="projectData.attachments"
            @edit="openFileModal"
            @delete="deleteAttachment"
          />
        </v-col>
      </v-row>

      <MembersTableCard :members="projectData.contracts" />

      <RecruitmentsTableCard :recruitments="projectData.recruitments" @delete="deleteElement" />
    </div>

    <ConfirmDialog ref="confirmDialog" />

    <EditTextModal
      v-model="isNotesModalVisible"
      :description="projectData.notes || ''"
      @add="addNotes"
    />

    <AttachmentsModal
      v-model="isFileModalVisible"
      :attachments="projectData.attachments"
      :isSubmitProcessing="isAttachmentProcessing"
      @addAttachments="addAttachments"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import ProjectDetailsForm from '@/components/Projects/ProjectDetailsForm.vue';
import MembersTableCard from '@/components/Projects/Show/MembersTableCard.vue';
import RecruitmentsTableCard from '@/components/Projects/Show/RecruitmentsTableCard.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import ProjectFinanceSummary from '@/components/ProjectFinanceSummary.vue';
import ProjectNotesCard from '@/components/ProjectNotesCard.vue';
import EditTextModal from '@/components/EditTextModal.vue';
import AttachmentsCard from '@/components/AttachmentsCard.vue';
import AttachmentsModal from '@/components/AttachmentsModal.vue';

export default {
  metaInfo() {
    return {
      title: `Project - ${this.projectData.name}`,
    };
  },

  components: {
    PageHeader,
    ProjectDetailsForm,
    MembersTableCard,
    RecruitmentsTableCard,
    ConfirmDialog,
    ProjectFinanceSummary,
    ProjectNotesCard,
    AttachmentsCard,
    EditTextModal,
    AttachmentsModal,
  },

  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      submitName: 'Save changes',
      isNotesModalVisible: false,
      isFileModalVisible: false,
    };
  },

  computed: {
    ...mapState('project', [
      'projectData',
      'projectRecentSummary',
      'isAttachmentSubmitProcessing',
    ]),

    ...mapState('attachment', ['isAttachmentProcessing']),
  },

  created() {
    this.fetchProject(this.id);
    this.fetchProjectRecentSummary(this.id);
  },

  methods: {
    ...mapActions('project', [
      'fetchProject',
      'updateProject',
      'clearProject',
      'fetchProjectRecentSummary',
      'addProjectNotes',
    ]),

    ...mapActions('attachment', [
      'uploadAttachments',
      'deleteAttachments',
    ]),

    update(project) {
      this.updateProject(project)
        .then(() => {
          this.$refs.projectDetailsForm.clearForm();
        });
    },

    deleteElement(id) {
      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.$store.dispatch('recruitment/deleteRecruitment', id)
              .then(() => {
                this.fetchProject(this.id);
              });
          }
        });
    },

    addNotes(notes) {
      this.addProjectNotes(notes);
      this.updateProject(this.projectData);
    },

    addAttachments(files) {
      const attachmentData = {
        attachments: files,
        apiType: 'client',
        id: this.id,
        recordType: 'project',
      };

      this.uploadAttachments(attachmentData)
        .then(() => {
          this.isFileModalVisible = false;
          this.fetchProject(this.id);
        });
    },

    deleteAttachment(attachmentId) {
      const attachmentData = {
        id: attachmentId,
        apiType: 'client',
      };

      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteAttachments(attachmentData)
              .then(() => {
                this.fetchProject(this.id);
              });
          }
        });
    },

    openNotesModal() {
      this.isNotesModalVisible = true;
    },

    openFileModal() {
      this.isFileModalVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped></style>
